import React, { useMemo, Suspense, useCallback, useEffect } from "react";
import "./styles.css";
import MegaScroll from "./components/MegaScroll";
import { GlobalHeader } from "./components/GlobalHeader";
import { NavigationProvider, useNavigation } from "./context/NavigationContext";
import { LazyCanvasLoader } from "./components/LazyCanvasLoader";
import { slides } from "./config/slides";

const slideComponents = {
    home: slides.find(s => s.key === "home")?.lazyComponent,
    "1234": slides.find(s => s.key === "1234")?.lazyComponent,
    info: slides.find(s => s.key === "info")?.lazyComponent,
};

const AppContent = () => {
    const { activeSlideKey, setActiveSlideIndex, scrollToIndex } = useNavigation();

    const renderSlides = useMemo(
        () => slides.filter(s => s.type === "slide"),
        []
    );

    const handleScrollChange = useCallback((index: number) => {
        const slide = renderSlides[index];
        if (slide) {
            const globalIndex = slides.findIndex(s => s.key === slide.key);
            setActiveSlideIndex(globalIndex);
            if (slide.hash) {
                window.history.replaceState(null, "", slide.hash);
            }
        }
    }, [renderSlides, setActiveSlideIndex]);

    const currentRenderIndex = useMemo(() => {
        return renderSlides.findIndex(s => s.key === activeSlideKey);
    }, [renderSlides, activeSlideKey]);

    const prev = useCallback(() => {
        if (currentRenderIndex > 0) {
            const prevSlide = renderSlides[currentRenderIndex - 1];
            const globalIndex = slides.findIndex(s => s.key === prevSlide.key);
            scrollToIndex(currentRenderIndex - 1);
            setActiveSlideIndex(globalIndex);
            if (prevSlide.hash) {
                window.history.replaceState(null, "", prevSlide.hash);
            }
        }
    }, [currentRenderIndex, renderSlides, scrollToIndex, setActiveSlideIndex]);

    const next = useCallback(() => {
        if (currentRenderIndex < renderSlides.length - 1) {
            const nextSlide = renderSlides[currentRenderIndex + 1];
            const globalIndex = slides.findIndex(s => s.key === nextSlide.key);
            scrollToIndex(currentRenderIndex + 1);
            setActiveSlideIndex(globalIndex);
            if (nextSlide.hash) {
                window.history.replaceState(null, "", nextSlide.hash);
            }
        }
    }, [currentRenderIndex, renderSlides, scrollToIndex, setActiveSlideIndex]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "ArrowRight") {
                next();
            } else if (e.key === "ArrowLeft") {
                prev();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [prev, next]);

    const children = useMemo(() => renderSlides.map((slide: any) => {
        // @ts-ignore
        const LazyComp = slideComponents[slide.key];
        if (!LazyComp) return null;
        return (
            <Suspense key={slide.key} fallback={<LazyCanvasLoader />}>
                {/* @ts-ignore */}
                <LazyComp />
            </Suspense>
        );
    }), [renderSlides]);

    return (
        <>
            <GlobalHeader activeSlide={activeSlideKey} />
            <MegaScroll onChange={handleScrollChange}>
                {children}
            </MegaScroll>
        </>
    );
};

const App = () => {
    return (
        <NavigationProvider slides={slides}>
            <AppContent />
        </NavigationProvider>
    );
};

export default App;
